import { useRouter } from 'next/router'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { HiOutlineLocationMarker, HiOutlineViewGrid } from 'react-icons/hi'

import { SelectRebrand } from 'src/components/atoms/SelectRebrand'
import { ParagraphNoSpace } from 'src/components/atoms/Typography'
import { buildRegionOptions } from 'src/components/molecules/RegionSelect/helpers'

import { useListRegionsForFormQuery } from 'src/config/generated/graphql'
import { useFilters } from 'src/hooks/useFilters'
import { usePagination } from 'src/hooks/usePagination'
import ProjectGridSkeleton from 'src/pages/planting-projects/ProjectGridSkeleton'
import ProjectGridViewRebrand from 'src/pages/planting-projects/ProjectGridViewRebrand'
import ProjectMapSkeleton from 'src/pages/planting-projects/ProjectMapSkeleton'
import PublicMapView from 'src/pages/planting-projects/PublicMapView'
import SeasonToggleRebrand from 'src/pages/planting-projects/SeasonToggleRebrand'
import ViewToggle from 'src/pages/planting-projects/ViewToggle'
import {
  ProjectView,
  buildQueryInput
} from 'src/pages/planting-projects/helpers'
import { ALL_SELECT_OPTION } from 'src/utils/form'
import { getPurposeOptions } from 'src/utils/projects'
import { trpc } from 'src/utils/trpc'

const SORT_OPTIONS = [
  { label: 'Latest', value: 'ApplicationCreatedOn' },
  { label: 'Name (A - Z)', value: 'NameAtoZ' },
  { label: 'Name (Z - A)', value: 'NameZtoA' },
  { label: 'North to South', value: 'NorthtoSouth' },
  { label: 'South to North', value: 'SouthtoNorth' }
]

type OrderBy =
  | 'NameAtoZ'
  | 'NameZtoA'
  | 'NorthtoSouth'
  | 'SouthtoNorth'
  | 'ApplicationCreatedOn'

const PlantingProjectsSectionRebrand = () => {
  const [orderBy, setOrderBy] = useState<OrderBy>('ApplicationCreatedOn')
  const [selectedView, setSelectedView] = useState<string>(ProjectView.Grid)
  const [isActiveProjects, setIsActiveProjects] = useState<boolean>(true)

  const { limit, offset, resetPagination } = usePagination({ limit: 9 })

  const { filters, setFilters } = useFilters({
    supportedFilters: ['purpose', 'regionId']
  })

  const { data: projectListings, isLoading } = trpc.useQuery(
    [
      'projects.listProjects',
      buildQueryInput({
        limit,
        offset,
        purpose: filters.purpose,
        orderBy,
        selectedView: selectedView as ProjectView,
        regionId: filters.regionId,
        publicPageOnly: false,
        approvedApplicationsOnly: true,
        currentSeasonOnly: isActiveProjects
      })
    ],
    { refetchOnWindowFocus: false }
  )

  const router = useRouter()
  const { selectedView: viewFromQuery } = router.query as {
    selectedView: string
  }

  useEffect(() => {
    if (viewFromQuery) {
      setSelectedView(viewFromQuery)
    }
  }, [viewFromQuery])

  const purposeOptions = [
    { label: 'All purposes', value: ALL_SELECT_OPTION.value },
    ...getPurposeOptions()
  ]

  const { data: regionsData } = useListRegionsForFormQuery()
  const regionOptions = useMemo(
    () =>
      buildRegionOptions(
        regionsData?.Region.filter(r => r.Type === 'Region'),
        { label: 'All regions', value: ALL_SELECT_OPTION.value }
      ),
    [regionsData?.Region]
  )

  return (
    <div className='container mx-auto px-4 lg:px-20'>
      <div className='flex flex-col gap-10'>
        <div className='flex flex-col gap-5'>
          <div className='flex flex-col md:flex-row flex-wrap gap-2 items-stretch md:items-center'>
            <SelectRebrand
              id='purpose'
              className='w-full md:w-[268px] bg-white!'
              options={purposeOptions}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                resetPagination()
                setFilters({ purpose: e.target.value })
              }}
              value={filters.purpose as string}
            />
            <SelectRebrand
              id='regionId'
              className='w-full md:w-[268px] bg-white!'
              options={regionOptions}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                resetPagination()
                setFilters({ regionId: e.target.value })
              }}
              value={filters.regionId as string}
            />
            {selectedView === ProjectView.Grid && (
              <SelectRebrand
                id='sort'
                className='w-full md:w-[268px] bg-white!'
                options={SORT_OPTIONS}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  resetPagination()
                  setOrderBy(e.target.value as OrderBy)
                }}
                value={orderBy}
              />
            )}
            <SeasonToggleRebrand
              isCurrent={isActiveProjects}
              onChange={value => {
                setIsActiveProjects(value)
                resetPagination()
              }}
            />
            <ViewToggle
              id='project-view'
              value={selectedView as ProjectView}
              onChange={next => {
                setSelectedView(next)
                router.replace(
                  {
                    pathname: router.pathname,
                    query: { ...router.query, selectedView: next }
                  },
                  undefined,
                  { shallow: true }
                )
              }}
              options={[
                {
                  title: 'Grid view',
                  value: ProjectView.Grid,
                  icon: HiOutlineViewGrid,
                  label: 'Grid'
                },
                {
                  title: 'Map view',
                  value: ProjectView.Map,
                  icon: HiOutlineLocationMarker,
                  label: 'Map'
                }
              ]}
            />
          </div>
          <ParagraphNoSpace className='text-[20px]! m-0! font-bold'>
            {isLoading
              ? ' '
              : `${projectListings?.Count.aggregate?.count ?? 0} projects`}
          </ParagraphNoSpace>
        </div>

        {selectedView === ProjectView.Grid && isLoading && (
          <ProjectGridSkeleton />
        )}
        {selectedView === ProjectView.Grid &&
          !isLoading &&
          (projectListings?.Projects?.length ? (
            <ProjectGridViewRebrand projects={projectListings} />
          ) : (
            <div>We could not find any projects</div>
          ))}
        {selectedView === ProjectView.Map && isLoading && (
          <ProjectMapSkeleton />
        )}
        {selectedView === ProjectView.Map && !isLoading && (
          <PublicMapView projects={projectListings} />
        )}
      </div>
    </div>
  )
}

export default PlantingProjectsSectionRebrand
